import { FC, useCallback, type JSX } from 'react';

import useDC from '@eneco/dc/useDC';
import { useTranslation } from '@eneco/i18n';
import { Box, Card, Divider, Heading, InputSelect, Stack, Text, TextArea, TextLink } from '@eneco/sparky';
import { ChevronRightIcon, ColdIcon, ElectricityIcon, GasIcon, WarmthIcon, WaterWarmIcon } from '@eneco/sparky/icons';
import { BoxProps, InputSelectProps } from '@eneco/sparky/types';
import {
  DC_Domain_Models_Products_ProductType,
  DC_Repositories_Base_Enumerations_Label,
  Usages_Reading_CounterModel,
} from '@monorepo-types/dc';
import { Control, useController, useFormState, useWatch } from 'react-hook-form';

import { useMeterReadingsFlow } from '../../../state/MeterReadingsFlow.machine';
import { ReadingMeterModel } from '../../../state/MeterReadingsFlow.types';
import { FormValues } from '../../ConfirmationStep.schema';

const ProductBackgrounds: Partial<
  Record<
    DC_Repositories_Base_Enumerations_Label,
    Partial<Record<DC_Domain_Models_Products_ProductType, BoxProps['backgroundColor']>>
  >
> = {
  eneco: {
    gas: 'backgroundVarOne',
    electricity: 'backgroundVarFive',
    warmth: 'backgroundVarTwo',
    cooling: 'backgroundVarThree',
    tapwater: 'backgroundVarThree',
  },
  oxxio: {
    gas: 'backgroundSecondary',
    electricity: 'backgroundSecondary',
    warmth: 'backgroundSecondary',
    cooling: 'backgroundSecondary',
    tapwater: 'backgroundSecondary',
  },
  woonenergie: {
    gas: 'backgroundSecondary',
    electricity: 'backgroundSecondary',
    warmth: 'backgroundSecondary',
    cooling: 'backgroundSecondary',
    tapwater: 'backgroundSecondary',
  },
};

const icons: Partial<Record<DC_Domain_Models_Products_ProductType, JSX.Element>> = {
  gas: <GasIcon color="iconGas" />,
  warmth: <WarmthIcon color="iconHeat" />,
  electricity: <ElectricityIcon color="iconElectricity" />,
  cooling: <ColdIcon color="iconCooling" />,
  tapwater: <WaterWarmIcon color="iconWater" />,
};

type DeviationAttributes = Pick<InputSelectProps, 'options' | 'label'>;

export type MeterCardProps = {
  name: DC_Domain_Models_Products_ProductType;
  meter: ReadingMeterModel;
  control: Control<FormValues>;
};

const maxDeviationReasonExplanationLength = 80;

export const MeterCard: FC<MeterCardProps> = ({ name, meter, control }) => {
  const { label } = useDC();
  const { t } = useTranslation();
  const { state, send } = useMeterReadingsFlow();
  const watch = useWatch({ control });
  const { errors } = useFormState({ control });

  const background = ProductBackgrounds[label]?.[name] || 'backgroundSecondary';
  const hasHighDeviation = (counter: Usages_Reading_CounterModel) => counter.highBoundary < Number(counter.value);
  const hasLowDeviation = (counter: Usages_Reading_CounterModel) => counter.lowBoundary > Number(counter.value);
  const hasDeviation = !!meter.counters?.some(counter => hasHighDeviation(counter) || hasLowDeviation(counter));

  const { field: deviationReasonCodeField } = useController({
    control,
    name: `meters.${name}.deviationReasonCode`,
    defaultValue: hasDeviation ? meter.deviationReasonCode || '' : undefined,
  });

  const { field: deviationReasonExplanationField } = useController({
    control,
    name: `meters.${name}.deviationReasonExplanation`,
    defaultValue: meter.deviationReasonExplanation ?? '',
  });

  const hasDifferentReason = useCallback(
    (name: DC_Domain_Models_Products_ProductType) => watch.meters?.[name]?.deviationReasonCode === 'ANDERS',
    [watch],
  );

  const getDeviationTypeAttributes = useCallback(
    (meter: ReadingMeterModel): DeviationAttributes => {
      const highDeviation = meter.counters?.find(hasHighDeviation);
      const label = highDeviation
        ? t('deviationReasonCodeFormField.highLabel')
        : t('deviationReasonCodeFormField.lowLabel');
      const reasons =
        (highDeviation ? state.context.reading?.deviationReasonsHigh : state.context.reading?.deviationReasonsLow) ||
        [];

      const options = reasons.map((reason: { key?: string; value?: string }) => ({
        label: reason.value || '',
        value: reason.key,
      }));

      return {
        label,
        options,
      };
    },
    [state.context.reading?.deviationReasonsHigh, state.context.reading?.deviationReasonsLow, t],
  );

  const onEdit = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
      type: 'GOTO_STEP',
      target: `METER_READINGS.${name.toUpperCase()}`,
    };
    send(payload);
  }, [name, send]);

  return (
    <Card key={name}>
      <Box padding="6">
        <Stack gap="6">
          <Stack direction="row" gap="4" alignY="center">
            <Box borderRadius="s" padding="4" backgroundColor={background}>
              {icons[name]}
            </Box>
            <Heading as="h3" size="XS">
              {t(`${name}ProductLabel`)}
            </Heading>
          </Stack>

          <Stack gap="2">
            {meter?.counters?.map(counter => (
              <Stack key={counter.counterIdentification} gap="2">
                <Stack direction="row" alignX="justify" alignY="center">
                  <Text weight="bold" size="BodyS">
                    {counter.name}
                  </Text>
                  <Text size="BodyM">
                    {counter.value} {counter.denotation}
                  </Text>
                </Stack>
                {hasHighDeviation(counter) && <Text color="textBrand">{t('deviationHighWarningText')}</Text>}
                {hasLowDeviation(counter) && <Text color="textBrand">{t('deviationLowWarningText')}</Text>}
                <Divider emphasis="low" />
              </Stack>
            ))}
          </Stack>

          <Stack gap="10">
            <TextLink emphasis="high" onClick={onEdit} leftIcon={<ChevronRightIcon />}>
              {t('editButtonLabel')}
            </TextLink>

            {hasDeviation && (
              <Stack gap="6">
                <InputSelect
                  {...deviationReasonCodeField}
                  {...getDeviationTypeAttributes(meter)}
                  error={
                    errors?.meters?.[name]?.deviationReasonCode?.type &&
                    t(`deviationReasonCodeFormField.${errors.meters[name]?.deviationReasonCode?.message}Message`)
                  }
                  placeholder={t('deviationReasonCodeFormField.placeholder')}
                />

                {hasDifferentReason(name) && (
                  <Stack>
                    <TextArea
                      maxLength={maxDeviationReasonExplanationLength}
                      hint={t('deviationReasonExplanationFormField.hint', {
                        max: maxDeviationReasonExplanationLength,
                        current: watch.meters?.[name]?.deviationReasonExplanation?.length || 0,
                      })}
                      rows={6}
                      label={t('deviationReasonExplanationFormField.label')}
                      {...deviationReasonExplanationField}
                      error={
                        errors?.meters?.[name]?.deviationReasonExplanation?.type &&
                        t(
                          `deviationReasonExplanationFormField.${errors.meters[name]?.deviationReasonExplanation?.message}Message`,
                        )
                      }
                    />
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
};
